import { useQueryClient } from '@tanstack/react-query';
import useMakesQuery from 'hooks/useMakesQuery';
import useTokenQuery from 'hooks/useTokenQuery';
import i18next from 'i18next';
import { createContext, FC, ReactNode, useContext, useEffect } from 'react';

import { Config, Make, StatusCodes, StatusMessages, StatusTransitionCodes, User } from '../api/types';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { isTestingEnvironment } from '../helpers/environments';
import useConfigQuery from '../hooks/useConfigQuery';
import useStatusMessagesQuery from '../hooks/useStatusMessagesQuery';
import useStatusQuery from '../hooks/useStatusQuery';
import useStatusTransitionQuery from '../hooks/useStatusTransitionQuery';
import useUserQuery from '../hooks/useUserQuery';
import useUsersQuery from '../hooks/useUsersQuery';

interface UserContextInterface {
  isMember: Function;
  login: () => void;
  logout: () => Promise<void>;
  config: Config;
  statusCodes: StatusCodes;
  statusMessages: StatusMessages;
  statusTransitions: StatusTransitionCodes;
  user: User;
  opUsers: Array<User>;
  makes: Array<Make>;
  error?: Error | null;
}

const UserContext = createContext<UserContextInterface>({} as UserContextInterface);

const UserContextProvider: FC<{ children?: ReactNode }> = (props) => {
  const queryClient = useQueryClient();
  const userQuery = useUserQuery();
  const configQuery = useConfigQuery();
  const statusCodeQuery = useStatusQuery();
  const statusTransitionsQuery = useStatusTransitionQuery();
  const statusMessagesQuery = useStatusMessagesQuery();
  const usersQuery = useUsersQuery();
  const makesQuery = useMakesQuery();

  const login = () => {
    queryClient.invalidateQueries({ queryKey: useTokenQuery.getKey() });
  };

  const logout = async () => {
    queryClient.setQueryData(useTokenQuery.getKey(), null);
  };

  const isMember = (permission: string) => {
    if (isTestingEnvironment()) {
      return true;
    } else if (userQuery.data?.permissions) {
      return userQuery.data?.permissions?.includes(permission);
    }
  };

  useEffect(() => {
    if (userQuery.data?.language) {
      i18next.changeLanguage(userQuery.data?.language);
    }
  }, [userQuery?.data]);

  if (
    userQuery.isLoading ||
    configQuery.isLoading ||
    statusCodeQuery.isLoading ||
    statusTransitionsQuery.isLoading ||
    statusMessagesQuery.isLoading ||
    usersQuery.isLoading ||
    makesQuery.isLoading
  ) {
    return <LoadingSpinner />;
  }

  let value: UserContextInterface = {
    login,
    logout,
    isMember,
    statusCodes: statusCodeQuery.data!,
    statusTransitions: statusTransitionsQuery.data!,
    statusMessages: statusMessagesQuery.data!,
    user: userQuery.data!,
    error: userQuery.error!,
    opUsers: usersQuery.data!,
    config: configQuery.data!,
    makes: makesQuery.data!,
  };

  return <UserContext.Provider value={value} {...props} />;
};

const useUserContext = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('Trying to access userContext outside of UserContextProvider');
  }
  return context;
};

export { UserContextProvider, useUserContext };
